import { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate, generatePath, Outlet } from 'react-router-dom';
import {
  ButtonPseudo,
  ButtonPrimary,
  SidePanel,
  SidePanelType,
  IconLoader,
  Popup,
  PermissionEnum,
  usePrevious,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useCreateCustomFeeMutation,
  useLazyGetTargetAccountQuery,
  useDeleteCustomFeeMutation,
} from '../store/services/customers';
import { clearFeeFormState, getFee } from '../store/slices/tariffs/form';
import FeeForm from '../../../components/partial/tariff/fee-form';
import {
  CustomerType,
  FeeLog,
  CustomFeeFormValues,
  FeeAmountTypeEnum,
  FeeItem,
  CustomerStatus,
  FeeItemFeeType,
} from '../types';

import ROUTES from '../../../routes';

import cn from '../customers.module.css';

const FeeFormHOC = ({ customerType }: { customerType: CustomerType }) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
    setValue,
    setError,
  } = useForm<CustomFeeFormValues>();
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [logs, setLogs] = useState<FeeLog[]>([]);
  const [defaultFormValues, setDefaultFormValues] =
    useState<CustomFeeFormValues>();
  const [defaultFee, setDefaultFee] = useState<FeeItem>();
  const [popupVisible, setPopupVisible] = useState(false);
  const [isRestoredToDefault, setIsRestoredToDefault] = useState(false);
  const [regularFee, setRegularFee] = useState(false);
  const [feeTypes, setFeeTypes] = useState<FeeItemFeeType[]>([]);

  const fields = watch();
  const prevFeeAmountType = usePrevious(fields.feeAmountType);
  const feeCurrencyId = watch('feeCurrencyId');
  const feeCascadeCurrenciesIds = watch('feeCascadeCurrenciesIds');

  const {
    customersDictionaries: { activePermissions, baseCurrency, currencies },
    customerFeeForm: { fee },
    individualCustomers: {
      info: {
        general: { baseInformation },
      },
    },
    corporateCustomers: {
      info: {
        general: { baseInformation: corporateBaseInformation },
      },
    },
  } = useAppSelector((state) => state);

  let info = baseInformation;

  if (customerType === CustomerType.CORPORATE) {
    info = corporateBaseInformation;
  }

  const [
    createCustomFee,
    { isLoading: requestInProgress, isSuccess: onSubmitSuccess },
  ] = useCreateCustomFeeMutation();
  const [getTargetAccount, { isLoading }] = useLazyGetTargetAccountQuery();

  const { customerId, feeId } = useParams();

  const getTargetAccountAction = useCallback(
    async (id: string, body: CustomFeeFormValues) => {
      const response = (await getTargetAccount(id)) as {
        data: { data: { fullNumber: string } };
      };

      if (response.data) {
        reset({
          ...body,
          feeTargetAccountNumber: response.data.data.fullNumber,
        });
      }
    },
    [],
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  let link: string;

  if (customerType === CustomerType.INDIVIDUAL) {
    link = ROUTES.CUSTOMERS_INDIVIDUAL_TARIFFS;
  } else {
    link = ROUTES.CUSTOMERS_CORPORATE_TARIFFS;
  }

  const [deleteFee, { isSuccess, isLoading: deleteInProgress }] =
    useDeleteCustomFeeMutation();

  const onSubmit = handleSubmit((values) => {
    if (!values.feeAmountType) {
      setError('feeAmountType', {
        type: 'custom',
        message: 'Write-off type is required',
      });
    } else if (
      values.feeAmountType &&
      values.feeAmountType === 'FIXED' &&
      !values.feeFixedAmount
    ) {
      setError('feeFixedAmount', {
        type: 'custom',
        message: 'Write-off value is required',
      });
    } else if (
      values.feeAmountType === 'PERCENT' &&
      (!values.feePercentAmount || !values.feePercentMinAmount)
    ) {
      if (!values.feePercentAmount) {
        setError('feePercentAmount', {
          type: 'custom',
          message: 'Write-off value is required',
        });
      }

      if (!values.feePercentMinAmount) {
        setError('feePercentMinAmount', {
          type: 'custom',
          message: 'Minimum amount allowed',
        });
      }
    } else {
      const body: CustomFeeFormValues = {
        feeId: values.feeId,
        name: values.name,
        feeTargetAccountNumber: values.feeTargetAccountNumber,
        feeAmountType: values.feeAmountType,
      };

      if (values.feeAmountType === FeeAmountTypeEnum.FIXED) {
        body.feeFixedAmount = Number(values.feeFixedAmount);
      } else {
        body.feePercentAmount = Number(values.feePercentAmount);

        if (values.feePercentMinAmount) {
          body.feePercentMinAmount = Number(values.feePercentMinAmount);
        }

        if (values.feePercentMaxAmount) {
          body.feePercentMaxAmount = Number(values.feePercentMaxAmount);
        }
      }

      if (values?.feeCurrencyId) {
        body.feeCurrencyId = values?.feeCurrencyId;
      }

      if (values?.feeCascadeCurrenciesIds) {
        body.feeCascadeCurrenciesIds = values?.feeCascadeCurrenciesIds;
      }

      if (isModified) {
        deleteFee({
          id: customerId as string,
          feeId: feeId as string,
        });
      }

      createCustomFee({ body, id: customerId as string });
    }
  });

  const onRestoreDefault = () => {
    setIsRestoredToDefault(true);

    reset(defaultFormValues);
    setIsModified(false);
  };

  const deleteFeeAction = () => {
    deleteFee({
      id: customerId as string,
      feeId: feeId as string,
    });

    setIsRestoredToDefault(false);
  };

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(
        generatePath(link, {
          customerId,
        }),
      );
    }, 100);
  }, []);

  useEffect(() => {
    if (onSubmitSuccess) {
      handleClose();
    }
  }, [onSubmitSuccess]);

  useEffect(() => {
    if (
      prevFeeAmountType &&
      fields.feeAmountType !== prevFeeAmountType &&
      !isRestoredToDefault
    ) {
      reset({
        ...fields,
        feePercentAmount: 0,
        feeFixedAmount: 0,
        feePercentMinAmount: 0,
        feePercentMaxAmount: 0,
      });
    }
  }, [fields, prevFeeAmountType, reset, isRestoredToDefault]);

  useEffect(() => {
    if (isSuccess && defaultFee) {
      const body: CustomFeeFormValues = {
        feeId: defaultFee.feeId,
        name: defaultFee.name,
        feePercentAmount: defaultFee.feePercentAmount,
        feeFixedAmount: defaultFee.feeFixedAmount,
        feePercentMinAmount: defaultFee.feePercentMinAmount,
        feePercentMaxAmount: defaultFee.feePercentMaxAmount,
        feeAmountType: defaultFee.feeAmountType,
        feeType: defaultFee.fee.id,
      };

      if (defaultFee?.feeCurrencyId) {
        body.feeCurrencyId = defaultFee?.feeCurrencyId;
      }

      if (defaultFee?.feeCascadeCurrenciesIds) {
        body.feeCascadeCurrenciesIds = defaultFee?.feeCascadeCurrenciesIds;
      }

      getTargetAccountAction(defaultFee.feeTargetAccountNumber, body);

      setIsModified(false);
      setDefaultFormValues(body);

      setTimeout(() => {
        setIsRestoredToDefault(false);
      }, 3000);
    }
  }, [isSuccess, defaultFee]);

  useEffect(() => {
    if (fee) {
      const currentDefaultFee = info?.defaultFees?.find(
        (item) => item.fee.id === fee.values?.fee.id,
      );

      if (currentDefaultFee) {
        setDefaultFee(currentDefaultFee);
      }

      if (fee?.values?.fee?.feeType === 'REGULAR') {
        setRegularFee(true);
      }

      const body: CustomFeeFormValues = {
        feeId: fee.values?.feeId as string,
        name: fee.values?.name as string,
        feePercentAmount: fee.values?.feePercentAmount,
        feeFixedAmount: fee.values?.feeFixedAmount,
        feePercentMinAmount: fee.values?.feePercentMinAmount,
        feePercentMaxAmount: fee.values?.feePercentMaxAmount,
        feeAmountType: fee.values?.feeAmountType as string,
        feeType: fee.values?.fee.id,
      };

      const defaultBody: CustomFeeFormValues = {
        feeId: currentDefaultFee?.feeId as string,
        name: currentDefaultFee?.name as string,
        feePercentAmount: currentDefaultFee?.feePercentAmount,
        feeFixedAmount: currentDefaultFee?.feeFixedAmount,
        feePercentMinAmount: currentDefaultFee?.feePercentMinAmount,
        feePercentMaxAmount: currentDefaultFee?.feePercentMaxAmount,
        feeAmountType: currentDefaultFee?.feeAmountType as string,
        feeType: currentDefaultFee?.fee.id,
      };

      if (fee.values?.isModified) {
        setIsModified(true);
      }

      if (fee?.values?.feeCurrencyId) {
        body.feeCurrencyId = fee?.values?.feeCurrencyId;
        defaultBody.feeCurrencyId = currentDefaultFee?.feeCurrencyId;
      }

      if (fee?.values?.feeCascadeCurrenciesIds) {
        body.feeCascadeCurrenciesIds = fee?.values?.feeCascadeCurrenciesIds;
        defaultBody.feeCascadeCurrenciesIds =
          currentDefaultFee?.feeCascadeCurrenciesIds;
      }

      setDefaultFormValues(defaultBody);

      getTargetAccountAction(
        fee.values?.feeTargetAccountNumber as string,
        body,
      );

      if (fee.logs) {
        setLogs(fee?.logs);
      }
    }
  }, [fee]);

  useEffect(() => {
    if (info?.fees && !fee) {
      const currentFeeItem = info?.fees?.find((item) => item.id === feeId);

      if (currentFeeItem) {
        const currentDefaultFee = info?.defaultFees?.find(
          (item) => item.fee.id === currentFeeItem?.fee.id,
        );

        getFee(currentFeeItem);

        if (currentFeeItem.fee.feeType === 'REGULAR') {
          setRegularFee(true);
        }

        const body: CustomFeeFormValues = {
          feeId: currentFeeItem.feeId,
          name: currentFeeItem.name,
          feePercentAmount: currentFeeItem.feePercentAmount,
          feeFixedAmount: currentFeeItem.feeFixedAmount,
          feePercentMinAmount: currentFeeItem.feePercentMinAmount,
          feePercentMaxAmount: currentFeeItem.feePercentMaxAmount,
          feeAmountType: currentFeeItem.feeAmountType,
          feeType: currentFeeItem.fee.id,
        };

        const defaultBody: CustomFeeFormValues = {
          feeId: currentDefaultFee?.feeId as string,
          name: currentDefaultFee?.name as string,
          feePercentAmount: currentDefaultFee?.feePercentAmount,
          feeFixedAmount: currentDefaultFee?.feeFixedAmount,
          feePercentMinAmount: currentDefaultFee?.feePercentMinAmount,
          feePercentMaxAmount: currentDefaultFee?.feePercentMaxAmount,
          feeAmountType: currentDefaultFee?.feeAmountType as string,
          feeType: currentDefaultFee?.fee.id,
        };

        if (currentFeeItem?.feeCurrencyId) {
          body.feeCurrencyId = currentFeeItem.feeCurrencyId;
          defaultBody.feeCurrencyId = currentDefaultFee?.feeCurrencyId;
        }

        if (currentFeeItem?.feeCascadeCurrenciesIds) {
          body.feeCascadeCurrenciesIds = currentFeeItem.feeCascadeCurrenciesIds;
          defaultBody.feeCascadeCurrenciesIds =
            currentDefaultFee?.feeCascadeCurrenciesIds;
        }

        setDefaultFormValues(defaultBody);

        if (currentFeeItem?.isModified) {
          setIsModified(true);
        }

        if (currentFeeItem?.fee) {
          setFeeTypes([currentFeeItem?.fee]);
        }

        getTargetAccountAction(currentFeeItem.feeTargetAccountNumber, body);
      }
    }
  }, [fee, info?.fees]);

  useEffect(() => {
    setPanelIsOpen(true);

    return () => {
      dispatch(clearFeeFormState());
    };
  }, [dispatch, feeId, feeTypes]);

  let buttonText;
  let content;
  let transactionLink;

  if (feeId) {
    buttonText = 'Save';

    if (customerType === CustomerType.INDIVIDUAL) {
      transactionLink = generatePath(
        ROUTES.CUSTOMERS_INDIVIDUAL_TARIFFS_CREATE_TRANSACTION,
        { customerId: customerId as string, feeId },
      );
    } else {
      transactionLink = generatePath(
        ROUTES.CUSTOMERS_CORPORATE_TARIFFS_CREATE_TRANSACTION,
        { customerId: customerId as string, feeId },
      );
    }
  } else {
    buttonText = 'Add Fee';
  }

  const organizationStatus =
    customerType === CustomerType.INDIVIDUAL
      ? baseInformation?.status
      : corporateBaseInformation?.status;

  if (isLoading) {
    content = (
      <div className={cn.customerFeeFormLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <FeeForm
        feeTypes={feeTypes}
        control={control}
        errors={errors}
        onClose={() => setPanelIsOpen(false)}
        feeId={feeId}
        link={transactionLink}
        logs={logs}
        defaultFormValues={defaultFormValues}
        values={fields}
        isModified={isModified}
        onRestoreDefault={onRestoreDefault}
        baseCurrency={baseCurrency}
        currencies={currencies}
        isEditable={activePermissions?.includes(
          customerType === CustomerType.INDIVIDUAL
            ? PermissionEnum.INDIVIDUAL_CUSTOMERS_EDIT_TARIFF_ACTION
            : PermissionEnum.CORPORATE_CUSTOMERS_EDIT_TARIFF_ACTION,
        )}
        regularFee={regularFee}
        feeCurrencyId={feeCurrencyId}
        feeCascadeCurrenciesIds={feeCascadeCurrenciesIds}
        setValue={setValue}
        status={organizationStatus}
      />
    );
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.Accounts}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={() => {
          if (isDirty) {
            setPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        sidepanelExpandedKey="customers-fee"
        title={feeId ? 'Edit fee' : 'Add fee to customer tariff'}
        footer={
          <>
            {activePermissions?.includes(
              customerType === CustomerType.INDIVIDUAL
                ? PermissionEnum.INDIVIDUAL_CUSTOMERS_EDIT_TARIFF_ACTION
                : PermissionEnum.CORPORATE_CUSTOMERS_EDIT_TARIFF_ACTION,
            ) &&
              organizationStatus !== CustomerStatus.CLOSED && (
                <ButtonPrimary
                  text={
                    requestInProgress || deleteInProgress
                      ? 'Loading...'
                      : buttonText
                  }
                  disabled={
                    Boolean(!fields?.name && !fields.feeType) ||
                    isLoading ||
                    deleteInProgress
                  }
                  onClick={() =>
                    isRestoredToDefault ? deleteFeeAction() : onSubmit()
                  }
                  bold
                />
              )}
            <ButtonPseudo
              text="Close"
              onClick={() => {
                if (isDirty) {
                  setPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="Changes have not been saved"
        description="Close without saving?"
        onCancelClick={() => setPopupVisible(false)}
        onOkClick={() => {
          reset(defaultFormValues);
          setPopupVisible(false);
          handleClose();
        }}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={popupVisible}
        parent={document.getElementById('save-alert-popup') as HTMLElement}
      />
      <div id="save-alert-popup" />
      <Outlet />
    </>
  );
};

export default FeeFormHOC;
